import clsx from 'clsx';
import { ReactElement, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { deepEquals } from '@vestico/utils';

import { UnitType } from '@vestico/model';

import style from './EntryField.module.css';
import { EntryFieldGeneric } from './EntryFieldGeneric';
import { EntryFieldInput, EntryValueConfig } from './EntryFieldInput';
import { HelpPopover } from './HelpPopover';
import { SegmentedControl } from './SegmentedControl';

// An entry option is for instance 'kg'
// Each field is is represented through a value config
export interface EntryOption {
  name: string;
  unitType: UnitType;
  valueConfigs: EntryValueConfig[];
  standardize: (value: number[]) => number;
  convert: (value: number) => number[];
}

// An entry configuration is for instance weight which comprises of kg and lbs as options
export interface EntryFieldConfiguration {
  id: string;
  name: string;
  options: EntryOption[];
  selectedOptionIndex: number;
  value?: number;
  minValue: number;
  maxValue: number;
  didUpdateConfig?: (config: EntryFieldConfiguration) => void;
  errorText: string;
  showErrorIfNeeded: boolean;
  allowOptionsConversion: boolean;
}

const EntryFieldConfigurationContext = createContext<EntryFieldConfiguration>({} as EntryFieldConfiguration);

export interface EntryFieldProps {
  configuration: EntryFieldConfiguration;
  helpText?: string | ReactElement;
  additionalError?: string;
}

export const EntryField = ({ configuration, helpText, additionalError }: EntryFieldProps) => {
  const [forceUpdate, setForceUpdate] = useState(false);
  const { id, name, options, allowOptionsConversion, selectedOptionIndex, errorText, showErrorIfNeeded, value, didUpdateConfig } = configuration;
  const optionNames = useMemo(
    () =>
      options.map((entryOption: EntryOption) => {
        return entryOption.name;
      }),
    [options]
  );

  const updateIndex = useCallback(
    (newIndex: number) => {
      // Don't update if indexes didn't change
      if (newIndex === selectedOptionIndex) {
        return;
      }

      const newConfig = { ...configuration };
      if (!allowOptionsConversion) {
        newConfig.value = undefined;
      }

      newConfig.selectedOptionIndex = newIndex;

      didUpdateConfig?.(newConfig);
    },
    [selectedOptionIndex, configuration, allowOptionsConversion, didUpdateConfig]
  );

  let segmentedControl;
  if (optionNames.length > 1) {
    segmentedControl = (
      <SegmentedControl configId={id} options={optionNames} selectedOptionIndex={selectedOptionIndex} didUpdateOption={updateIndex} />
    );
  } else {
    segmentedControl = <></>;
  }

  const showError = useMemo(() => additionalError || (showErrorIfNeeded && !value), [showErrorIfNeeded, value, additionalError]);

  useEffect(() => {
    setForceUpdate(false);
  }, [forceUpdate]);

  const onUpdateValue = useCallback(() => setForceUpdate(true), []);

  return (
    <EntryFieldConfigurationContext.Provider value={configuration}>
      <div>
        <EntryFieldGeneric
          title={name}
          error={showError ? additionalError ?? errorText : undefined}
          id={id}
          content={
            <>
              <div className={clsx(style.fieldWrapper)}>
                <EntryFieldOption fieldID={id} didUpdateValue={onUpdateValue} />
                <HelpPopover helpText={helpText} id={id} />
              </div>
              {segmentedControl}
            </>
          }
        />
      </div>
    </EntryFieldConfigurationContext.Provider>
  );
};

interface EntryFieldOptionProps {
  fieldID: string;
  didUpdateValue: () => void;
}

// Renders all fields for a given option
const EntryFieldOption = ({ fieldID, didUpdateValue }: EntryFieldOptionProps) => {
  const configContext = useContext(EntryFieldConfigurationContext);
  const { options, value } = configContext;
  const selectedOption = options[configContext.selectedOptionIndex];
  const { valueConfigs, convert } = selectedOption;

  const initConvertedValues = useCallback(() => {
    if (value === undefined) {
      const emptyValues: string[] = valueConfigs.map(() => {
        return '';
      });
      return emptyValues;
    }
    const convertedValues = convert(value).map((convertedValue: number) => {
      return convertedValue.toString();
    });
    return convertedValues;
  }, [convert, value, valueConfigs]);

  const [convertedValues, setConvertedValues] = useState(initConvertedValues);

  const standardizedValueForConvertedValues = useCallback(
    (valuesToConvert: string[]) => {
      const convertedValuesAsNumbers = valuesToConvert.map((convertedValue) => {
        return Number(convertedValue);
      });

      return selectedOption.standardize(convertedValuesAsNumbers);
    },
    [selectedOption]
  );

  const updateConvertedValue = useCallback(
    (newIndexValueString: string, index: number) => {
      const indexConfig = valueConfigs[index];
      const { digitsCount, maxValue } = indexConfig;

      const newConvertedValues = [...convertedValues];
      newConvertedValues[index] = newIndexValueString;
      const newIndexValue = Number(newIndexValueString);

      const newStandardizedValue = standardizedValueForConvertedValues(newConvertedValues);

      const regex = new RegExp(`^\\d{0,${digitsCount}}$`);
      const isWithinRange = !Number.isNaN(newIndexValue) && newIndexValue <= maxValue;
      const isValidNumber = isWithinRange && regex.test(`${newIndexValueString}`);

      let validConvertedValues: string[];
      let validStandardizedValue: number;

      if (!isValidNumber) {
        console.debug(`input is not valid ${newIndexValueString}, ${newStandardizedValue}, ${configContext.minValue}, ${configContext.maxValue}`);
        validConvertedValues = convertedValues;
        validStandardizedValue = standardizedValueForConvertedValues(validConvertedValues);
      } else {
        validConvertedValues = newConvertedValues;
        validStandardizedValue = newStandardizedValue;
      }

      setConvertedValues(validConvertedValues);
      configContext.value = validStandardizedValue;
      didUpdateValue();

      return validConvertedValues[index];
    },
    [configContext, convertedValues, didUpdateValue, standardizedValueForConvertedValues, valueConfigs]
  );

  useEffect(() => {
    const newConvertedValues = initConvertedValues();
    if (!deepEquals(newConvertedValues, convertedValues)) {
      setConvertedValues(newConvertedValues);
      if (newConvertedValues.length !== selectedOption.valueConfigs.length) {
        console.error(
          `Converted values count must be equal to selected option! values: ${newConvertedValues}, option: ${JSON.stringify(
            selectedOption.valueConfigs
          )}`
        );
      }
    }
  }, [configContext, configContext.selectedOptionIndex, configContext.value, convertedValues, initConvertedValues, selectedOption.valueConfigs]);

  const showError = useMemo(() => configContext.showErrorIfNeeded && !configContext.value, [configContext.showErrorIfNeeded, configContext.value]);

  const onInput = useCallback(
    (index: number) => (convertedValue: string) => {
      return updateConvertedValue(convertedValue, index);
    },
    [updateConvertedValue]
  );

  return (
    <div className={clsx(style.option)}>
      {selectedOption.valueConfigs.map((valueConfig: EntryValueConfig, index: number) => {
        return (
          <EntryFieldInput
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            fieldID={fieldID}
            currentValue={convertedValues[index]}
            valueConfig={valueConfig}
            showError={showError}
            onInput={onInput(index)}
          />
        );
      })}
    </div>
  );
};
